import { TransportLocationTypes, cn, findTransportLocation } from "@elit-hair/elitklinik-shared";
import { Autocomplete, Box, InputAdornment, TextField, Typography } from "@mui/material";
import { GridApiCommon } from "@mui/x-data-grid";
import React, { ReactElement, SyntheticEvent, createElement, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IconType } from "react-icons/lib";
import { TbBuildingAirport } from "react-icons/tb";

import { transportLocationStyles } from "@/constants/transferLocations";
import { HotelClinicListItem } from "@/pages/HotelPlanning/clinic-arrival/types";
import {
    IHotelPlanningDepartureClinicItem,
    IHotelPlanningDepartureResponseItem,
} from "@/schemas/hotelPlanningDepartureSchema";
import { IPlanPassenger } from "@/types/tasks.types";

import HistoryInfo from "./HistoryInfo";

export type EditRowModel = IPlanPassenger<"api"> | IHotelPlanningDepartureResponseItem | HotelClinicListItem;

export type EditAutoCompleteOptionCompProps = {
    option: { label: string; value: string | number; type?: TransportLocationTypes };
    isFlightArrival?: boolean;
    options?: { label: string; value: string | number; type?: TransportLocationTypes }[];
    value: string;
};

type EditAutoCompleteProps<T extends EditRowModel> = {
    row: T;
    field: string;
    value: string | number;
    api: GridApiCommon;
    icon: IconType;
    /** When set, used for DataGrid cell APIs instead of `row.id` (e.g. `uniqueIdentifier` as row id) */
    gridRowId?: string;
    placeholder?: string;
    options: { label: string; value: string | number; type?: TransportLocationTypes }[];
    optionComp: React.ComponentType<EditAutoCompleteOptionCompProps>;
    groupBy?: (option: { label: string; value: string | number; type?: TransportLocationTypes }) => string;
    renderHistoryComp: (value: string | number) => ReactElement<{}>;
    excludeValue?: string | number;
    translation?: boolean;
    labelPrefix?: string;
    processRowUpdate: (newRow: T, oldRow: T) => T;
    inputClassName?: string;
};

function EditAutoCompleteInner<T extends EditRowModel>({
    options,
    optionComp,
    row,
    value,
    field,
    api,
    icon,
    gridRowId,
    placeholder,
    renderHistoryComp,
    groupBy,
    excludeValue,
    translation = false,
    labelPrefix,
    processRowUpdate,
    inputClassName,
}: EditAutoCompleteProps<T>) {
    const { t } = useTranslation();
    const id = gridRowId ?? ("id" in row ? (row.id ?? "") : (row as IHotelPlanningDepartureClinicItem).operationId);
    const activeLocation = useMemo(
        () => options.find((item) => item.value === value || item.label === value) || null,
        [options, value]
    );
    const excludedOptions = useMemo(
        () =>
            excludeValue
                ? options.filter((item) => findTransportLocation(excludeValue)?.value !== item.value)
                : options,
        [options, excludeValue]
    );

    const handleChange = (_event: SyntheticEvent, newValue: { label: string; value: string | number } | null) => {
        processRowUpdate({ ...row, [field]: newValue?.value || newValue }, row);
        api.setEditCellValue({
            id,
            field: field,
            value: newValue?.value ?? newValue ?? null,
        });
    };

    return (
        <div className=" w-full h-full relative flex items-center">
            <Autocomplete
                className="!w-full !h-full !my-auto flex items-center"
                classes={{
                    inputRoot: cn(" !h-[40px] !my-auto !w-full", inputClassName),
                    option: "text-2xl !py-1",
                }}
                autoFocus
                sx={() => ({
                    "label + &": {
                        marginTop: 20,
                        display: "none",
                    },
                    "& .MuiInputBase-input": {
                        paddingBottom: 0,
                        paddingTop: 0,
                        fontSize: 12,
                    },
                })}
                groupBy={groupBy}
                renderGroup={(params) => {
                    const type = transportLocationStyles.find((style) => style.label === params.group);
                    return (
                        <Box key={params.key}>
                            <Box
                                className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200"
                                style={{ backgroundColor: type?.bg }}
                            >
                                {type &&
                                    createElement(type.component(), {
                                        className: "w-4 h-4",
                                        style: { color: type.text },
                                    })}
                                <Typography variant="body2" className="font-medium" style={{ color: type?.text }}>
                                    {params.group}
                                </Typography>
                            </Box>
                            <Box>{params.children}</Box>
                        </Box>
                    );
                }}
                value={activeLocation as { label: string; value: string | number }}
                options={excludedOptions}
                getOptionLabel={(option) =>
                    translation
                        ? t(labelPrefix ? `${labelPrefix}${option.label || ""}` : option.label)
                        : option.label || ""
                }
                onChange={handleChange}
                renderOption={(props, option) => {
                    const { key, ...optionProps } = props;

                    return (
                        <Box
                            key={key}
                            component="li"
                            {...optionProps}
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                padding: 0,
                                gap: 0,
                            }}
                        >
                            {createElement(optionComp, {
                                option,
                                isFlightArrival: "isFlightArrival" in row ? row.isFlightArrival : false,
                                options,
                                value: String(option.value ?? ""),
                            })}
                        </Box>
                    );
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder={t("placeholders.select", {
                            field: t(placeholder || field),
                        })}
                        variant="filled"
                        sx={{
                            "& .MuiInputBase-input": {
                                transform: "translateY(-7px)",
                            },
                        }}
                        className="!w-full"
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                                <InputAdornment position="end" className=" !-translate-x-1 !-translate-y-2">
                                    {createElement(icon || TbBuildingAirport)}
                                </InputAdornment>
                            ),
                        }}
                    />
                )}
            />

            <HistoryInfo renderHistoryComp={renderHistoryComp} id={id} field={field} />
        </div>
    );
}

const MemoizedEditAutoComplete = memo(EditAutoCompleteInner) as <T extends EditRowModel>(
    props: EditAutoCompleteProps<T>
) => React.ReactElement;

export default MemoizedEditAutoComplete;
